// src/components/landingpage/pp-terminalx/Lightbox.jsx
//
// The full-screen image viewer behind every "Click to zoom" on the TerminalX
// page (enhancements log, 18 Oct 2026). Fit / 160% / 240% / 320% zoom, scroll
// to pan, previous and next across the whole gallery, Esc to close. The arrow
// keys act only while the viewer is open, so the page's own keyboard
// behaviour is unaffected.
import { useEffect, useState } from 'react';
import './Lightbox.css';

export const ZOOMS = [1, 1.6, 2.4, 3.2];

const Chevron = ({ dir }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="square" aria-hidden="true">
    <polyline points={dir < 0 ? '15 5 8 12 15 19' : '9 5 16 12 9 19'} />
  </svg>
);

/**
 * @param images  [{ src, cap }] in gallery order
 * @param index   which one is showing
 * @param onIndex (next index) => void
 * @param onClose () => void
 */
export default function Lightbox({ images, index, onIndex, onClose }) {
  const [z, setZ] = useState(0);
  const n = images.length;
  const img = images[index];
  const move = (d) => { setZ(0); onIndex((index + d + n) % n); };
  const zoom = (d) => setZ((v) => Math.max(0, Math.min(ZOOMS.length - 1, v + d)));

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight') move(1);
      else if (e.key === 'ArrowLeft') move(-1);
      else return;
      e.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, n]);

  const fit = z === 0;
  const scale = ZOOMS[z];

  return (
    <div role="dialog" aria-modal="true" aria-label={img.cap} className="rw-lb">
      <div className="rw-lb__bar">
        <span className="rw-lb__counter">{index + 1} / {n}</span>
        <span className="rw-lb__caption">{img.cap}</span>
        <span className="rw-lb__zoom">
          <button type="button" onClick={() => zoom(-1)} aria-label="Zoom out" className="rw-lb__zoom-btn">&minus;</button>
          <span className="rw-lb__zoom-label">{fit ? 'Fit' : `${Math.round(scale * 100)}%`}</span>
          <button type="button" onClick={() => zoom(1)} aria-label="Zoom in" className="rw-lb__zoom-btn">+</button>
        </span>
        <button type="button" onClick={onClose} aria-label="Close" className="rw-lb__close">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="square" aria-hidden="true">
            <line x1="5" y1="5" x2="19" y2="19" /><line x1="19" y1="5" x2="5" y2="19" />
          </svg>
        </button>
      </div>
      <div className="rw-lb__stage">
        <div className={'rw-lb__scroll' + (fit ? ' is-fit' : '')}>
          <img
            src={img.src}
            alt={img.cap}
            onClick={() => setZ((v) => (v < ZOOMS.length - 1 ? v + 1 : 0))}
            className={'rw-lb__img' + (fit ? ' is-fit' : '')}
            style={fit ? undefined : { width: `${scale * 100}%` }}
            data-cursor={z < ZOOMS.length - 1 ? 'in' : 'out'}
          />
        </div>
        <button type="button" onClick={() => move(-1)} aria-label="Previous image" className="rw-lb__nav rw-lb__nav--prev"><Chevron dir={-1} /></button>
        <button type="button" onClick={() => move(1)} aria-label="Next image" className="rw-lb__nav rw-lb__nav--next"><Chevron dir={1} /></button>
      </div>
      <div className="rw-lb__hint">Click the image or use + to zoom, then scroll to pan. Arrow keys move between images, Esc closes.</div>
    </div>
  );
}
